package com.partejar.app.feature.gestantes.data.remote

import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.feature.gestantes.data.GestanteListItemData
import com.partejar.app.feature.gestantes.data.GestanteListStage
import com.partejar.app.feature.gestantes.data.GestantesPageData
import com.partejar.app.feature.gestantes.data.GestantesPayloadException
import com.partejar.app.feature.gestantes.data.GestanteClinicalDetail
import com.partejar.app.feature.gestantes.data.GestanteAddress
import com.partejar.app.feature.gestantes.data.GestanteComorbidity
import com.partejar.app.feature.gestantes.data.GestanteExam
import com.partejar.app.feature.gestantes.data.GestanteExamResult
import com.partejar.app.feature.gestantes.data.GestanteExamStatus
import com.partejar.app.feature.gestantes.data.GestanteExamType
import com.partejar.app.feature.gestantes.data.GestanteExamAnalysis
import com.partejar.app.feature.gestantes.data.GestanteExamUnit
import com.partejar.app.feature.gestantes.data.*

class GestantesRemoteMapper {
    fun map(response: GestantesPageDto): GestantesPageData = GestantesPageData(
        items = response.content.map(::mapItem),
        page = response.page,
        totalElements = response.totalElements,
        totalPages = response.totalPages,
        isLast = response.last
    )

    fun mapDetail(item: GestanteDetailDto): GestanteClinicalDetail {
        val stage = GestanteListStage.entries.firstOrNull { it.apiValue == item.statusAtual } ?: throw GestantesPayloadException()
        return GestanteClinicalDetail(
            id = item.pacienteId,
            name = item.nomeGestante,
            babyName = item.nomeBebe,
            address = GestanteAddress(item.enderecoCep, item.enderecoLogradouro, item.enderecoNumero,
                item.enderecoComplemento, item.enderecoBairro, item.enderecoCidade, item.enderecoUf),
            dueDate = item.dataProvavelParto,
            gestationalWeeks = item.idadeGestacional?.semanas,
            gestationalDays = item.idadeGestacional?.dias,
            daysUntilDueDate = item.diasAteDpp,
            maternity = item.maternidadeEscolha,
            prenatalType = item.tipoPreNatal,
            livesInBh = item.moraEmBh,
            responsible = item.responsavelPrincipal?.nome,
            stage = stage,
            comorbidities = item.comorbidades.map(::mapComorbidity),
            delivery = item.parto?.let(::mapDelivery)
        )
    }

    fun mapDelivery(item: GestanteDeliveryDto) = GestanteDelivery(item.id, item.dataParto, item.horaParto,
        GestanteDeliveryRoute.entries.firstOrNull { it.apiValue == item.viaParto } ?: throw GestantesPayloadException(), item.localParto,
        GestanteMaternalOutcome.entries.firstOrNull { it.apiValue == item.desfechoMaterno } ?: throw GestantesPayloadException(),
        GestanteNeonatalOutcome.entries.firstOrNull { it.apiValue == item.desfechoNeonatal } ?: throw GestantesPayloadException(), item.observacao, item.registradoPor)

    fun mapComorbidity(item: GestanteComorbidadeDto): GestanteComorbidity = GestanteComorbidity(
        id = item.id,
        description = item.descricao,
        observation = item.observacao,
        active = item.ativa
    )

    fun mapExamType(item: GestanteExamTypeDto): GestanteExamType = GestanteExamType(
        id = item.id,
        name = item.nome,
        category = item.categoria,
        code = item.codigo,
        active = item.ativo,
        analyses = item.analises.map { analysis ->
            GestanteExamAnalysis(analysis.id, analysis.codigo, analysis.nome, analysis.definicao,
                analysis.loincCodigo, analysis.unidadeObrigatoria, analysis.evolucaoHabilitada, analysis.ativo,
                analysis.unidades.map { unit ->
                    GestanteExamUnit(unit.id, unit.codigo, unit.codigoUcum, unit.rotulo, unit.padrao, unit.ativo)
                })
        }
    )

    fun mapExam(item: GestanteExamDto): GestanteExam = GestanteExam(
        id = item.id,
        type = mapExamType(item.tipo),
        performedAt = item.realizadoEm,
        status = mapExamStatus(item.statusAvaliacao),
        observation = item.observacao,
        registeredBy = item.registradoPor,
        results = item.resultados.map { result ->
            GestanteExamResult(
                id = result.id,
                parameter = result.parametro,
                value = result.valor,
                unit = result.unidade,
                reference = result.referencia,
                status = mapExamStatus(result.status),
                analysisId = result.analiseId,
                analysisUnitId = result.analiseUnidadeId,
                freeResult = result.resultadoLivre,
                evolutionEnabled = result.evolucaoHabilitada
            )
        }
    )

    private fun mapExamStatus(value: String): GestanteExamStatus =
        GestanteExamStatus.entries.firstOrNull { it.apiValue == value }
            ?: throw GestantesPayloadException()

    private fun mapItem(item: GestanteListItemDto): GestanteListItemData {
        val stage = GestanteListStage.entries.firstOrNull { it.apiValue == item.statusAtual }
            ?: throw GestantesPayloadException()
        val pendingLabels = item.situacaoOperacional.pendencias.map(::pendingLabel)
        val actionRequired = when (item.situacaoOperacional.status) {
            "EM_DIA" -> {
                if (pendingLabels.isNotEmpty()) throw GestantesPayloadException()
                null
            }
            "ACAO_PENDENTE" -> pendingLabels.ifEmpty { listOf("Ação pendente") }.joinToString(", ")
            else -> throw GestantesPayloadException()
        }
        return GestanteListItemData(
            id = item.pacienteId.toString(),
            name = item.nomeGestante,
            babyName = item.nomeBebe,
            gestationalWeeks = item.idadeGestacional?.semanas,
            gestationalDays = item.idadeGestacional?.dias,
            dueDate = item.dataProvavelParto?.let(::formatDueDate),
            responsible = item.responsavelPrincipal?.nome,
            stage = stage,
            actionRequired = actionRequired,
            deliveryDate = item.dataParto?.let { formatBrazilianDate(it) ?: throw GestantesPayloadException() }
        )
    }

    private fun pendingLabel(value: String): String = when (value) {
        "OFICINA_PENDENTE" -> "Oficina de preparação pendente"
        "FOLLOW_UP_PENDENTE" -> "Follow-up pendente"
        "ASSINATURA_PENDENTE" -> "Assinatura pendente"
        else -> throw GestantesPayloadException()
    }

    private fun formatDueDate(value: String): String {
        val parts = value.split('-')
        if (parts.size != 3) throw GestantesPayloadException()
        val month = parts[1].toIntOrNull() ?: throw GestantesPayloadException()
        val day = parts[2].toIntOrNull() ?: throw GestantesPayloadException()
        if (day !in 1..31) throw GestantesPayloadException()
        val monthLabel = MONTHS.getOrNull(month - 1) ?: throw GestantesPayloadException()
        return "${day.toString().padStart(2, '0')} $monthLabel"
    }

    private companion object {
        val MONTHS = listOf("jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez")
    }
}
